import { IFileResponse } from '../../../../interfaces/businessData';
import {
  IMetricsOnboardingByPeriod,
  IMetricsRanking,
  IMetricsBetterfliesRanking,
  IMetricsBenefits,
  IMetricsDonationsSummary,
  IMetricsChallengesSummary,
  IMetricsChallengeByType,
  IMetricsChallengeRanking,
  IMetricsDonationsChartData,
  IMetricsProtectionByCases,
  IMetricsProtectionCoverage,
  IMetricsProtectionInsurance
} from '../../../../interfaces/metrics';
import {
  MOCK_DOWNLOAD_FILE_RESPONSE,
  MOCK_METRICS_ONBOARDING_BY_PERIOD,
  MOCK_METRICS_RANKING,
  MOCK_METRICS_BETTERFLIES_RANKING,
  MOCK_METRICS_BENEFITS,
  MOCK_METRICS_DONATIONS_SUMMARY,
  MOCK_METRICS_CHALLENGES_SUMMARY,
  MOCK_METRICS_CHALLENGES_BY_TYPE,
  MOCK_METRICS_CHALLENGES_RANKING,
  MOCK_METRICS_DONATIONS_RANKING,
  MOCK_METRICS_DONATIONS_BY_CAUSES,
  MOCK_METRICS_PROTECTION,
  MOCK_METRICS_PROTECTION_COVERAGE,
  MOCK_METRICS_PROTECTION_INSURANCE
} from '../../../../mocks/metrics';
import { HttpServiceServer } from '../../../http/httpService';

export class MetricsByCompany {
  static async getMonthlyReport(companyId: string, date?: string): Promise<IFileResponse> {
    const response = await HttpServiceServer.get<IFileResponse>(
      `companies/${companyId}/monthly-report`,
      { params: { date } },
      MOCK_DOWNLOAD_FILE_RESPONSE
    );
    return response.data;
  }

  static async getOnboardingMetricsByPeriod(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsOnboardingByPeriod> {
    const response = await HttpServiceServer.get<IMetricsOnboardingByPeriod>(
      `companies/${companyId}/metrics/onboarding`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_ONBOARDING_BY_PERIOD }
    );
    return response.data;
  }

  static async getBetterfliesRankingMetrics(
    companyId: string,
    period: string,
    date?: string,
    limit?: number,
    offset?: number
  ): Promise<IMetricsBetterfliesRanking> {
    const response = await HttpServiceServer.get<IMetricsBetterfliesRanking>(
      `companies/${companyId}/metrics/betterflies-ranking`,
      { params: { period: period, month: date, limit, offset } },
      { data: MOCK_METRICS_BETTERFLIES_RANKING }
    );
    return response.data;
  }

  static async getBenefitsRankingMetrics(companyId: string, period: string, date?: string): Promise<IMetricsRanking> {
    const response = await HttpServiceServer.get<IMetricsRanking>(
      `companies/${companyId}/metrics/benefits-ranking`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_RANKING }
    );
    return response.data;
  }

  static async getDonationsRankingMetrics(companyId: string, period: string, date?: string): Promise<IMetricsRanking> {
    const response = await HttpServiceServer.get<IMetricsRanking>(
      `companies/${companyId}/metrics/donations-ranking`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_DONATIONS_RANKING }
    );
    return response.data;
  }

  static async getDonationsSummaryMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsDonationsSummary> {
    const response = await HttpServiceServer.get<IMetricsDonationsSummary>(
      `companies/${companyId}/metrics/donations-summary`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_DONATIONS_SUMMARY }
    );
    return response.data;
  }

  static async getChallengesSummaryMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsChallengesSummary> {
    const response = await HttpServiceServer.get<IMetricsChallengesSummary>(
      `companies/${companyId}/metrics/challenges-summary`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_CHALLENGES_SUMMARY }
    );
    return response.data;
  }

  static async getChallengesByTypeMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsChallengeByType[]> {
    const response = await HttpServiceServer.get<IMetricsChallengeByType[]>(
      `companies/${companyId}/metrics/challenges-by-type`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_CHALLENGES_BY_TYPE }
    );
    return response.data;
  }

  static async getChallengesRankingMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsChallengeRanking[]> {
    const response = await HttpServiceServer.get<IMetricsChallengeRanking[]>(
      `companies/${companyId}/metrics/challenges-ranking`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_CHALLENGES_RANKING }
    );
    return response.data;
  }

  static async getDonationsByCausesMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsDonationsChartData> {
    const response = await HttpServiceServer.get<IMetricsDonationsChartData>(
      `companies/${companyId}/metrics/donations-by-causes`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_DONATIONS_BY_CAUSES }
    );
    return response.data;
  }

  static async getProtectionsByCaseMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsProtectionByCases> {
    const response = await HttpServiceServer.get<IMetricsProtectionByCases>(
      `companies/${companyId}/metrics/insurance-metrics`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_PROTECTION }
    );
    return response.data;
  }

  static async getProtectionCoverageMetrics(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsProtectionCoverage> {
    const response = await HttpServiceServer.get<IMetricsProtectionCoverage>(
      `/companies/${companyId}/metrics/coverage-progression`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_PROTECTION_COVERAGE }
    );
    return response.data;
  }

  static async getInsuranceProtection(
    companyId: string,
    period: string,
    date?: string
  ): Promise<IMetricsProtectionInsurance> {
    const response = await HttpServiceServer.get<IMetricsProtectionInsurance>(
      `/companies/${companyId}/metrics/insurance-use-intention`,
      { params: { period: period, month: date } },
      { data: MOCK_METRICS_PROTECTION_INSURANCE }
    );
    return response.data;
  }

  static async getBenefitsMetrics(
    companyId: string,
    period: string,
    date?: string,
    locale?: string,
    benefitIds?: string
  ): Promise<IMetricsBenefits> {
    const response = await HttpServiceServer.get<IMetricsBenefits>(
      `companies/${companyId}/metrics/benefits`,
      { params: { period: period, month: date, locale: locale, benefitIds } },
      { data: MOCK_METRICS_BENEFITS }
    );
    return response.data;
  }
}

export default MetricsByCompany;
